import React, { useEffect, useState, useCallback, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { isEmpty } from 'lodash';
import { withRouter } from 'react-router-dom'

import * as actions from "../../../store/actions";

import {
  Paper, Table, TableBody, TableCell, TableFooter, TablePagination, TableRow, TextField, Typography, CircularProgress,
} from '@mui/material';

import EnhancedTableHead from "../../../UI/MaterialTableOverrides/EnhancedTableHead";
import TableActionButtonsLegacy from "../../../UI/MaterialTableOverrides/TableActionButtons-legacy";
import TablePaginationActionsWrapped from "../../../UI/MaterialTableOverrides/TablePaginationActions";
import TableTitle from '../../../UI/MaterialTableOverrides/TableTitle';
import { deleteCheckMessage } from "../../../utils/deleteCheckMessage";
import ModalSites from "./Modals/ModalSites";

const TableSites = props => {
  const dispatch = useDispatch();
  let textInput = useRef(null);

  const [page, setPage] = useState(1);
  const [alreadySerched, setAlreadySerched] = useState(false);
  const [limit, setLimit] = useState(5);
  const [searchText, setSearchText] = useState("");
  const [orderBy, setOrderBy] = useState("name");
  const [order, setOrder] = useState("asc");
  const [editedIndex, setEditedIndex] = useState(-1);
  const [areaEditText, setAreaEditText] = useState("");
  const [isAddingNewArea, setIsAddingNewArea] = useState(false);
  const [actionRow, setActionRow] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);

  const fetchSites = useCallback(
    () => dispatch(actions.fetchSitesByTenant(props.match.params.id, page, limit, searchText, order, orderBy)),
    [page, limit, searchText, order, orderBy]
  );

  const fetchCheckDelete = useCallback(
    (index) => dispatch(actions.checkDeleteSites(index)));

  const deleteSite = (index) => dispatch(actions.deleteSites(index))
  const updateSite = (index, body) => dispatch(actions.updateSites(index, body))
  const postSites = (body) => {dispatch(actions.postSites(body)); fetchSites();}

  const sites = useSelector(state => state.sites.sites);
  const checkDeleteSites = useSelector(state => state.sites.checkDelete);

  const loading = useSelector(state => state.sites.loading);

  const headRows = [
    { id: "actions", numeric: false, disablePadding: false, label: "Ações" },
    { id: "name", numeric: false, disablePadding: false, label: "Site" }
  ];

  useEffect(() => {
    fetchSites();
    setAlreadySerched(true);
  }, [page, limit, order, orderBy]);

  useEffect(() => {
    if (searchText !== "" || alreadySerched) {
      const timer = setTimeout(() => {
        if (searchText === textInput.current.value) {
          fetchSites();
        }
      }, 200);
      return () => {
        clearTimeout(timer);
      };
    }
  }, [searchText, textInput]);

  const handleChangePage = (e, page) => {
    setEditedIndex(-1);
    setPage(page + 1);
    setIsAddingNewArea(false)
  };

  const handleChangeRowsPerPage = e => {
    setLimit(e.target.value);
    setPage(1);
    setEditedIndex(-1);
    setIsAddingNewArea(false)
  };

  const handleRequestSort = (event, property) => {
    const isDesc = orderBy === property && order === "desc";
    setOrder(isDesc ? "asc" : "desc");
    setOrderBy(property);
  };

  const editComponent = (index, initialText) => {
    setEditedIndex(index);
    setAreaEditText(initialText);
    setActionRow('editing');
    setIsAddingNewArea(false)
  };

  const cancelEdit = () => {
    setEditedIndex(-1);
  };

  const deleteComponent = (index, objIndex) => {
    fetchCheckDelete(objIndex)
    setEditedIndex(index);
    setActionRow('deleting');
  };

  const updateComponent = (index) => {
    if (actionRow === 'editing') {
      if (areaEditText === '') return;
      updateSite(index, { name: areaEditText }).then((res) => {
        if (res === 'success') {
          setEditedIndex(-1);
          fetchSites();
        }
      })
    } else if (actionRow === 'deleting') {
      deleteSite(index).then((res) => {
        if (res === 'success') {
          setEditedIndex(-1);
          fetchSites();
        }
      })
    }
  };

  const editArea = () => {
    return (
      <TextField
        variant="standard"
        style={styles.editWidth}
        multiline
        id="area"
        value={areaEditText}
        margin="normal"
        onChange={event => {
          setAreaEditText(event.target.value.toUpperCase());
        }} />
    );
  };

  const styles = {
    table: {
      position: 'relative',
    },
    opacityContainer: {
      opacity: loading ? '0.3' : '1',
    },
    spinner: {
      position: 'absolute',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '100%',
      height: '100%',
      top: '-5px',
      opacity: '1',
      zIndex: 2,
    },
    noResults: {
      height: '100px',
    },
    deleteTextConfirm: {
      marginLeft: '-30px',
      fontWeight: 'bold',
    },
    editWidth: {
      width: '90%'
    }
  }

  let deleteMessage = 'Não pode ser deletado. Utilizado por';

  Object.keys(checkDeleteSites).map((key, index) => {
    return deleteMessage += `${!!index ? ' e' : ''} ${key} ${checkDeleteSites[key]}x`
  });

  return <>
  <ModalSites
    open={isModalOpen}
    onClose={() => setIsModalOpen(false)}
    postSites={postSites}
    tenantId={props.match.params.id}
  />
    <Paper>
      <TableTitle
        title='Sites'
        placeholder='Pesquisar por site'
        inputRef={textInput}
        value={searchText}
        onChange={event => {
          setPage(1);
          setSearchText(event.target.value);
        }}
        addRow={() => setIsModalOpen(true)}
      />
      <Table style={styles.table}>
        <tbody>
          <tr>
            <td>
              {loading &&
                <div style={styles.spinner}>
                  <CircularProgress />
                </div>}
              {sites.rows.length === 0 && !isAddingNewArea &&
                <div style={styles.noResults}>
                  {!loading && <Typography style={styles.spinner}>Nenhum resultado encontrado</Typography>}
                </div>}
            </td>
          </tr>
        </tbody>
        <EnhancedTableHead
          style={styles.opacityContainer}
          headRows={headRows}
          order={order}
          orderBy={orderBy}
          onRequestSort={handleRequestSort}
        />
        <TableBody style={styles.opacityContainer}>
          {sites.rows.map((area, index) => (
            <TableRow key={area._id}>
              <TableActionButtonsLegacy
                objContent={area}
                deleteComponent={deleteComponent}
                editComponent={editComponent}
                updateComponent={updateComponent}
                cancelEdit={cancelEdit}
                editedIndex={editedIndex}
                index={index}
                action={actionRow}
                checkDelete={isEmpty(checkDeleteSites)}
              />
              <TableCell>
                {index === editedIndex ?
                  actionRow === 'editing' ?
                    editArea() :
                    <Typography variant='h6' style={styles.deleteTextConfirm}>
                      {isEmpty(checkDeleteSites) ? "Deseja realmente deletar este item?" : deleteMessage}
                    </Typography> :
                  area.name
                }
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
        <TableFooter style={styles.opacityContainer}>
          <TableRow>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              count={sites.count}
              rowsPerPage={parseInt(limit)}
              page={page - 1}
              SelectProps={{
                native: true
              }}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              ActionsComponent={TablePaginationActionsWrapped}
            />
          </TableRow>
        </TableFooter>
      </Table>
    </Paper>
  </>;

}

export default withRouter(TableSites);